/**
 * The privileged Provider Interface editor (ADR-040 §D-4 / §D-5 / §D-10; Flow V
 * Frames 4–8).
 *
 * One scrollable form covering the whole lifecycle — *choose type → configure
 * connections → define filter → install trust material → start/stop* — for
 * `admin` / `support_engineer` only. Non-privileged sessions never render this
 * component; they get :func:`ProviderInterfaceSummary` instead, because the BFF
 * withholds the bind endpoints, demarcation IPs, cert references, conversion
 * profile and filter literals this form is made of.
 *
 * # Two constrained axes, where the egress twin has one (REQ-UI-161)
 *
 * The interface **type** constrains both ``trust_mode`` (who authenticates the
 * counterparty) and ``transport_security`` (what the wire does), and the same
 * rule governs each: a value the type does not permit is **absent from the
 * control with the reason stated**, never present-and-disabled; where only one
 * value is permitted the field renders as a **stated fact rather than a
 * one-option select**. A select with nothing to select is indistinguishable from
 * a broken control, and a greyed-out option claims a permission problem where
 * the truth is that the type fixes it.
 *
 * ⚠ **A stored value outside its type's permitted set is STATED, never
 * corrected.** Rendering the type's constraint instead would show a
 * valid-looking interface the server 422s, with the disagreement invisible on
 * both sides. Applied to *both* axes here.
 *
 * # Trust material discloses on trust mode FIRST, then transport security
 *
 * One level more than the egress twin, because ``tunnel`` short-circuits the
 * question entirely: Meridian presents and verifies nothing, so no certificate
 * field belongs on the form at all — only the demarcation record.
 *
 * # Save is not start
 *
 * The form's Save issues the whole-document PUT. Start/stop is a **different
 * button on a different endpoint**, so it lands as its own audited event and
 * takes effect without carrying whatever else is half-typed in the form. A new
 * interface is created stopped and there is no "save and start"
 * (REQ-PROVIDER-007).
 *
 * # Client-side validation mirrors, never replaces, the server's
 *
 * The checks here exist so an operator sees a duplicate connection name before a
 * 409, not so the client decides what is legal. The BFF re-validates everything
 * (ADR-009 defence-in-depth), and where the two ever disagree the server wins
 * and its message is what the form shows.
 *
 * Cross-references:
 *   - ADR-040 §2.4 / §2.5 (types + trust), §D-4, §D-5, §D-6, §D-10; ADR-023.
 *   - REQ-PROVIDER-004 / -006 / -007 / -016 / -028 / -029 / -030 / -035 / -040.
 *   - REQ-UI-159 / -160 / -161 / -162; Flow V Frames 4–8.
 */
import { useMemo, useState } from "react";
import { PeerSelect, PeerTextInput, PeerTokenList } from "@/peers/peer-form-primitives";
import { PeerSection } from "@/peers/peer-section";
import { StatePill, StateToggleButton } from "@/peers/peer-state-controls";
import { PeerSuppressionNote } from "@/peers/peer-suppression-note";
import { resolveEffectiveState, type ChainLink } from "@/peers/peer-effective-state";
import {
  CREATABLE_PROVIDER_INTERFACE_TYPES,
  PROVIDER_TRANSPORT_SECURITY_MODES,
  PROVIDER_TRUST_MODES,
  allowedProviderTransportSecurity,
  allowedTrustModes,
  findProviderInterfaceType,
  findProviderTransportSecurity,
  findTrustMode,
  providerTransportSecurityFixedReason,
  trustModeFixedReason,
  type InterfaceState,
  type TransportSecurity,
  type TrustMode,
} from "@/providers/provider-catalog";
import { ProviderCertUpload } from "@/providers/provider-cert-upload";
import { useListenPortRanges } from "@/chrome/use-deployment-config";
import { portOutsidePublishedRange } from "@/lib/listen-port-range";
import { ProviderConnectionEditor } from "@/providers/provider-connection-editor";
import { ProviderFilterBuilder } from "@/providers/provider-filter-builder";
import { toProviderFilterConfig } from "@/providers/provider-filter-tree";
import {
  newProviderConnectionConfig,
  type ProviderInterfaceConfig,
  type ProviderInterfaceConnectionConfig,
} from "@/providers/provider-model";

/**
 * What the interface-level start/stop does, disclosed from the State header.
 *
 * Architect-specified wording (2026-10-10), and a standing paragraph until
 * then. Two facts kept — the control acts immediately rather than on Save, and
 * a stopped interface neither collects nor accumulates — and one dropped: that
 * the change lands as its own audit event. That was true and is still true,
 * but it describes what Meridian records rather than what the operator is
 * about to change, and the audit log is where an operator goes to see it.
 */
export const PROVIDER_INTERFACE_STATE_HELP =
  "Start/Stop takes effect immediately. A stopped interface does not collect, and provider " +
  "data are not queued.";

/** Props for {@link ProviderInterfaceEditor}. */
export interface ProviderInterfaceEditorProps {
  /** The interface being edited, as a complete PUT body. */
  value: ProviderInterfaceConfig;
  /** True when this interface has never been persisted (drives copy + state control). */
  isNew: boolean;
  onSave: (config: ProviderInterfaceConfig) => void;
  onDelete: () => void;
  onCancel: () => void;
  onSetState: (state: InterfaceState) => void;
  onSetConnectionState: (connectionId: string, state: InterfaceState) => void;
  /**
   * Delete one PERSISTED connection on the server, immediately (REQ-UI-159).
   *
   * Optional: a caller that does not supply it gets draft-only removal.
   */
  onDeleteConnection?: (connectionId: string) => void;
  savePending?: boolean;
  statePending?: boolean;
  /** Server-side error text to surface verbatim above the buttons. */
  saveError?: string | null;
  /** Names of the other interfaces in this provider, for the local uniqueness hint. */
  otherNames: readonly string[];
  /** Mints ids for new connections; injectable so tests are deterministic. */
  makeId?: () => string;
  /**
   * The owning chain, root-first — ``[provider]`` when mounted in the dialog.
   *
   * Threaded down to the connection level so a connection under a stopped
   * provider says so rather than reading as collecting (Flow V §5a).
   */
  ancestors?: readonly ChainLink[];
}

/** Render the full provider-interface editor. */
export function ProviderInterfaceEditor({
  value,
  isNew,
  onSave,
  onDelete,
  onCancel,
  onSetState,
  onSetConnectionState,
  onDeleteConnection,
  savePending = false,
  statePending = false,
  saveError,
  otherNames,
  makeId = () => crypto.randomUUID(),
  ancestors = [],
}: ProviderInterfaceEditorProps): React.ReactElement {
  const [draft, setDraft] = useState<ProviderInterfaceConfig>(value);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [seed, setSeed] = useState<ProviderInterfaceConfig>(value);

  /**
   * Re-seed when the selection changes, or when a save round-trips a new
   * server-side value (state flips arrive from the start/stop endpoint).
   *
   * ⚠ Done **during render** against a remembered seed, not in an effect — see
   * :func:`ProviderEditor` for the reasoning and the waiver-list note.
   *
   * ⚠ This compares `value` by **identity**, so the caller must keep it stable
   * across renders that did not change the underlying row. The interfaces
   * section memoizes it for exactly that reason: building the config inline
   * would mint a new object on every parent render, and the operator's
   * half-typed edits would be discarded by an unrelated sibling's pending-state
   * flip.
   */
  if (seed !== value) {
    setSeed(value);
    setDraft(value);
    setConfirmDelete(false);
  }

  /**
   * Which connections exist on the server.
   *
   * Derived from `value` — the document the server sent — and never from
   * `draft`, which also holds rows the operator just added. A draft-only row has
   * nothing to DELETE, and addressing the nested route with its id would 404 on
   * a control that looks identical to the one beside it.
   */
  const persistedConnectionIds = useMemo(
    () => new Set(value.connections.map((c) => c.connection_id)),
    [value.connections],
  );

  const descriptor = findProviderInterfaceType(draft.type_id);
  const payloadDomain = descriptor?.payloadDomain ?? "sapient";
  const patch = (next: Partial<ProviderInterfaceConfig>): void =>
    setDraft((current) => ({ ...current, ...next }));

  const nameError = useMemo(() => {
    const trimmed = draft.name.trim();
    if (trimmed === "") return "A name is required.";
    if (otherNames.includes(trimmed)) return "Another interface in this provider uses this name.";
    return null;
  }, [draft.name, otherNames]);

  const duplicateConnectionNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const connection of draft.connections) {
      const key = connection.name.trim();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([name]) => name));
  }, [draft.connections]);

  // REQ-UI-198: a `listen` port outside the published range is refused here
  // with the BFF's own wording, so Save is blocked for the same reason the
  // server would give. An unadvertised range (older BFF, config pending or
  // errored) blocks nothing — the server stays the authority.
  const publishedRange = useListenPortRanges()?.provider ?? null;
  const unpublishedListenPorts = useMemo(
    () =>
      draft.connections.filter(
        (connection) =>
          connection.role === "listen" &&
          portOutsidePublishedRange(connection.endpoint_port, publishedRange),
      ).length,
    [draft.connections, publishedRange],
  );

  const blocked =
    nameError !== null || duplicateConnectionNames.size > 0 || unpublishedListenPorts > 0;

  // ---- The two constrained axes (REQ-UI-161) --------------------------------
  const permittedTrust = allowedTrustModes(draft.type_id);
  const permittedWire = allowedProviderTransportSecurity(draft.type_id);
  const currentTrust = findTrustMode(draft.trust_mode);
  const currentWire = findProviderTransportSecurity(draft.transport_security);
  const trustFixedReason = trustModeFixedReason(draft.type_id);
  const wireFixedReason = providerTransportSecurityFixedReason(draft.type_id);
  /**
   * A stored value outside what this type permits — a cross-level disagreement,
   * shown as it is rather than coerced.
   *
   * Reachable three ways: a document written before a type's permitted set
   * narrowed, a row persisted by a newer release, and a type change that left
   * the value behind. **Never silently corrected**, because the row is what the
   * server holds and what assembly will refuse; a UI that quietly rendered the
   * type's constraint instead would show a valid-looking interface the server
   * 422s. Both facts are stated and the operator picks. The
   * single-permitted-value branch is suppressed in this state for the same
   * reason: it would assert the constraint as though the row satisfied it.
   */
  const trustDisagrees = !permittedTrust.includes(draft.trust_mode);
  const wireDisagrees = !permittedWire.includes(draft.transport_security);

  const isTunnel = draft.trust_mode === "tunnel";
  const isPlaintext = draft.transport_security === "plaintext";

  /**
   * Which trust fields apply — **trust mode first, then transport security**
   * (REQ-UI-161; Flow V Frame 7).
   *
   * Under ``tunnel`` Meridian presents and verifies nothing, so the certificate
   * fields do not belong on the form at all — the demarcation record replaces
   * them. Under ``mtls`` trust the wire mode then decides: ``plaintext``
   * presents and verifies nothing either; ``tls`` presents a Meridian identity
   * and verifies no client, so it needs no provider anchor; ``mtls`` uses all
   * three.
   */
  const showProviderAnchor = !isTunnel && draft.transport_security === "mtls";
  const showMeridianIdentity = !isTunnel && draft.transport_security !== "plaintext";

  /**
   * Re-resolve every type-constrained field when the type changes.
   *
   * Clearing the now-inapplicable values rather than hiding them, and it matters
   * more here than on egress because the type constrains *two* axes: a
   * `sapient-data-ingress` interface set to `mtls`/`mtls` and retyped to
   * `cds-ingress` would carry a pair that type's descriptor refuses, so
   * the save 422s on fields the operator can no longer see. Falling back to the
   * new type's own defaults keeps the document valid — and those defaults are
   * fail-safe by construction.
   */
  const setType = (type_id: string): void => {
    const next = findProviderInterfaceType(type_id);
    const nextTrust: TrustMode = allowedTrustModes(type_id).includes(draft.trust_mode)
      ? draft.trust_mode
      : (next?.defaultTrustMode ?? "mtls");
    const nextWire: TransportSecurity = allowedProviderTransportSecurity(type_id).includes(
      draft.transport_security,
    )
      ? draft.transport_security
      : (next?.defaultTransportSecurity ?? "mtls");
    patch({
      type_id,
      trust_mode: nextTrust,
      transport_security: nextWire,
      // Every dependent field follows the RESOLVED pair, not the type — the same
      // rule the fields render by, applied to what is persisted, so a type change
      // cannot leave a reference or an address behind that nothing displays.
      ...trustDependentFields(nextTrust, nextWire, draft),
      // The transport selection is the type's to permit: `raw-data-ingress` is
      // the one type with a choice, so anything else must return to its default.
      transport_id: type_id === draft.type_id ? draft.transport_id : null,
    });
  };

  const setTrustMode = (mode: string): void => {
    const next = mode as TrustMode;
    patch({
      trust_mode: next,
      ...trustDependentFields(next, draft.transport_security, draft),
    });
  };

  const setTransportSecurity = (mode: string): void => {
    const next = mode as TransportSecurity;
    patch({
      transport_security: next,
      ...trustDependentFields(draft.trust_mode, next, draft),
    });
  };

  const patchConnection = (index: number, next: ProviderInterfaceConnectionConfig): void =>
    patch({ connections: draft.connections.map((c, i) => (i === index ? next : c)) });

  return (
    <form
      data-testid="provider-interface-editor"
      // No scroll container of its own: the dialog is ONE scrollable pane with
      // sticky section headers (ADR-024 §4). A nested scroller here would strand
      // a section header inside a box the operator has to find before they can
      // scroll it.
      onSubmit={(event) => {
        event.preventDefault();
        if (!blocked) onSave({ ...draft, name: draft.name.trim() });
      }}
    >
      <div>
        <PeerSection id="provider-identity" title="Interface">
          <div className="flex flex-col gap-3">
            <PeerTextInput
              label="Name"
              value={draft.name}
              maxLength={128}
              onChange={(name) => patch({ name })}
              error={nameError}
              testId="provider-interface-name"
              placeholder="black-diamond-sapient"
              hint="Unique within this provider. Appears on the System display."
            />
            <PeerSelect
              label="Type"
              value={draft.type_id}
              testId="provider-interface-type"
              // Creatable types only: offering one that cannot then be started
              // would be an affordance leading nowhere. An interface already
              // configured with a reserved type never reaches this editor — it
              // renders read-only.
              options={CREATABLE_PROVIDER_INTERFACE_TYPES.map((t) => ({
                value: t.typeId,
                label: t.label,
              }))}
              onChange={setType}
              hint={descriptor?.description ?? `Unrecognized type "${draft.type_id}".`}
            />

            {/* ---- Axis 1: trust mode (REQ-UI-161) ---- */}
            {permittedTrust.length === 1 && !trustDisagrees ? (
              <div className="flex flex-col gap-0.5" data-testid="provider-trust-mode-fixed">
                <span className="text-body-sm text-text-secondary">Trust mode</span>
                <span className="text-body-sm text-text-primary">
                  {currentTrust?.label ?? draft.trust_mode}
                </span>
                <span className="text-meta text-text-muted">
                  {trustFixedReason ?? "Fixed by this interface type."}
                </span>
              </div>
            ) : (
              <PeerSelect
                label="Trust mode"
                value={draft.trust_mode}
                testId="provider-trust-mode"
                // The stored value is included even when the type forbids it, so
                // the control shows what the row actually says. Dropping it would
                // make the select render a DIFFERENT value than the document
                // holds — a silent rewrite on the next save.
                options={PROVIDER_TRUST_MODES.filter(
                  (m) => permittedTrust.includes(m.value) || m.value === draft.trust_mode,
                ).map((m) => ({ value: m.value, label: m.label }))}
                onChange={setTrustMode}
                hint={currentTrust?.hint}
              />
            )}
            {trustDisagrees && (
              <p
                role="alert"
                data-testid="provider-trust-mode-disagreement"
                className="rounded border border-warning px-2 py-1 text-meta text-warning"
              >
                This interface is stored as{" "}
                <strong>{currentTrust?.label ?? draft.trust_mode}</strong>, which its type does not
                permit — {trustFixedReason ?? "the type constrains this field."} It is shown as
                stored rather than corrected; saving it unchanged will be refused. Choose a
                permitted mode, or change the type.
              </p>
            )}
            {/* The whole of ADR-023, said once and in operator register. */}
            {isTunnel && (
              <p
                role="status"
                data-testid="provider-tunnel-delegation"
                className="rounded border border-warning px-2 py-1 text-meta text-warning"
              >
                {findTrustMode("tunnel")?.delegation}
              </p>
            )}

            {/* ---- Axis 2: transport security (REQ-UI-161) ---- */}
            {permittedWire.length === 1 && !wireDisagrees ? (
              <div
                className="flex flex-col gap-0.5"
                data-testid="provider-transport-security-fixed"
              >
                <span className="text-body-sm text-text-secondary">Transport security</span>
                <span className="text-body-sm text-text-primary">
                  {currentWire?.label ?? draft.transport_security}
                </span>
                <span className="text-meta text-text-muted">
                  {wireFixedReason ?? "Fixed by this interface type."}
                </span>
              </div>
            ) : (
              <PeerSelect
                label="Transport security"
                value={draft.transport_security}
                testId="provider-transport-security"
                options={PROVIDER_TRANSPORT_SECURITY_MODES.filter(
                  (m) => permittedWire.includes(m.value) || m.value === draft.transport_security,
                ).map((m) => ({ value: m.value, label: m.label }))}
                onChange={setTransportSecurity}
                hint={currentWire?.hint}
              />
            )}
            {wireDisagrees && (
              <p
                role="alert"
                data-testid="provider-transport-security-disagreement"
                className="rounded border border-warning px-2 py-1 text-meta text-warning"
              >
                This interface is stored as{" "}
                <strong>{currentWire?.label ?? draft.transport_security}</strong>, which its type
                does not permit — {wireFixedReason ?? "the type constrains this field."} It is shown
                as stored rather than corrected; saving it unchanged will be refused. Choose a
                permitted mode, or change the type.
              </p>
            )}
            {/* The consequence at SELECTION. Its twin at confirmation lives above
                the footer's Save — REQ-UI-161 requires both, in the same words,
                because they answer different questions and an operator who set
                this field rows ago is no longer looking at this one.
                ⚠ No `bg-destructive/10`: `text-destructive` over its own 10% tint
                composites to ~4.15:1 in the light theme, under REQ-UI-046's 4.5:1
                text bar; on the plain panel the same ink is ~5.06:1. The border
                carries the alarm as a non-text channel. (Measured on the egress
                twin's §5.11 pass — every unit test was green, because jsdom
                composites nothing.) */}
            {isPlaintext && (
              <p
                role="status"
                data-testid="provider-plaintext-consequence"
                className="rounded border border-destructive px-2 py-1 text-meta text-destructive"
              >
                {findProviderTransportSecurity("plaintext")?.consequence}
              </p>
            )}

            {/* ---- Conversion (REQ-UI-162) ----
                The PROFILE is privileged and this editor only ever renders for a
                privileged session, so it is unconditional here. The MODE is
                offered only once a profile is set: the model's `conv_needs_profile`
                CHECK refuses a non-`legacy` mode without one, so the control would
                otherwise lead straight to a 422. */}
            <PeerTextInput
              label="Conversion profile"
              value={draft.conversion_profile_ref ?? ""}
              mono
              onChange={(ref) => {
                const conversion_profile_ref = ref.trim() === "" ? null : ref;
                patch({
                  conversion_profile_ref,
                  // Clearing the profile must take the mode with it, or the save
                  // carries `engine` with no ref and is refused by the CHECK.
                  conversion_mode:
                    conversion_profile_ref === null ? "legacy" : draft.conversion_mode,
                });
              }}
              testId="provider-conversion-profile"
              placeholder="sha256:… — no profile installed on this interface"
              hint="The installed (provider schema, conversion spec) pair, by digest."
              error={
                // Named specifically because the server refuses it and the
                // operator cannot otherwise tell which of the two fields is wrong.
                descriptor?.requiresConversionProfile === true &&
                draft.conversion_profile_ref === null
                  ? "This interface type decodes through a conversion profile and cannot run without one."
                  : null
              }
            />
            {draft.conversion_profile_ref === null ? (
              <div className="flex flex-col gap-0.5" data-testid="provider-conversion-mode-fixed">
                <span className="text-body-sm text-text-secondary">Conversion mode</span>
                <span className="text-body-sm text-text-primary">
                  Legacy — the built-in decoder is authoritative
                </span>
                <span className="text-meta text-text-muted">
                  Install a profile before selecting another mode; a mode without a profile is
                  refused.
                </span>
              </div>
            ) : (
              <PeerSelect
                label="Conversion mode"
                value={draft.conversion_mode}
                testId="provider-conversion-mode"
                options={[
                  { value: "legacy", label: "Legacy — the built-in decoder is authoritative" },
                  {
                    value: "engine",
                    label: "Engine — the installed conversion profile is authoritative",
                  },
                ]}
                onChange={(mode) =>
                  patch({
                    conversion_mode: mode as ProviderInterfaceConfig["conversion_mode"],
                  })
                }
                hint={
                  draft.conversion_mode === "engine"
                    ? "What Meridian stores from this provider is decoded by the installed profile, not by built-in code."
                    : "What Meridian stores from this provider is decoded by Meridian's built-in code for its type."
                }
              />
            )}

            <PeerTextInput
              label="Description"
              value={draft.metadata?.description ?? ""}
              onChange={(description) => patch({ metadata: { ...draft.metadata, description } })}
              testId="provider-interface-description"
              hint="Free-form operator annotation — what this feed carries, and who operates it."
            />
            <label className="flex items-center gap-2 text-body-sm text-text-primary">
              <input
                type="checkbox"
                checked={draft.enabled}
                data-testid="provider-interface-enabled"
                onChange={(event) => patch({ enabled: event.target.checked })}
                className="h-3.5 w-3.5 cursor-pointer"
              />
              Enabled — the service loads this interface at all. Distinct from started/stopped.
            </label>
          </div>
        </PeerSection>

        <PeerSection
          id="provider-connections"
          title="Provider connections"
          action={
            <button
              type="button"
              data-testid="provider-add-connection"
              onClick={() =>
                patch({
                  connections: [...draft.connections, newProviderConnectionConfig(makeId())],
                })
              }
              className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
            >
              Add connection
            </button>
          }
        >
          {draft.connections.length === 0 ? (
            <p className="text-body-sm text-text-muted" data-testid="provider-no-connections">
              No connections. This interface is not listening anywhere and will receive nothing.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {draft.connections.map((connection, index) => (
                <ProviderConnectionEditor
                  key={connection.connection_id}
                  connection={connection}
                  index={index}
                  unsaved={isNew}
                  nameError={
                    duplicateConnectionNames.has(connection.name.trim())
                      ? "Duplicate connection name within this interface."
                      : null
                  }
                  onChange={(next) => patchConnection(index, next)}
                  onRemove={() =>
                    patch({ connections: draft.connections.filter((_, i) => i !== index) })
                  }
                  onDelete={
                    onDeleteConnection !== undefined &&
                    persistedConnectionIds.has(connection.connection_id)
                      ? () => onDeleteConnection(connection.connection_id)
                      : undefined
                  }
                  onToggleState={(next) => onSetConnectionState(connection.connection_id, next)}
                  statePending={statePending}
                  publishedRange={publishedRange}
                  suppression={resolveEffectiveState(
                    [...ancestors, { name: draft.name, state: draft.state }],
                    connection.state,
                  )}
                />
              ))}
            </div>
          )}
        </PeerSection>

        {/* Disclosed by TRUST MODE first, then transport security (REQ-UI-161;
            Flow V Frame 7). The section always renders — an interface that
            presents and verifies nothing states that, rather than the section
            vanishing and leaving an operator to wonder where the trust material
            went. */}
        <PeerSection id="provider-trust" title="Trust material">
          <div className="flex flex-col gap-3">
            {isTunnel ? (
              <>
                <p className="text-body-sm text-text-muted" data-testid="provider-trust-tunnel">
                  The network layer authenticates this provider, so Meridian presents and verifies
                  no certificates here.
                </p>
                <PeerTokenList
                  label="Demarcation addresses"
                  values={draft.demarcation_ips}
                  onChange={(demarcation_ips) => patch({ demarcation_ips })}
                  testId="provider-demarcation-ips"
                  placeholder="10.20.30.0/24"
                  // ⚠ The load-bearing sentence on this whole section. A list that
                  // LOOKS like an allow-list and is not is worse than no list: an
                  // operator who believes Meridian enforces it will not go and
                  // check that the network layer does.
                  hint="An operational record of where the protected path is expected to admit from — not an allow-list Meridian enforces, and not checked per message. Leaving it empty is a valid configuration."
                />
                {draft.demarcation_ips.length === 0 && (
                  <p
                    role="status"
                    data-testid="provider-demarcation-empty"
                    className="text-meta text-text-muted"
                  >
                    No addresses are recorded. That is accepted — the interface will start — and it
                    means nothing here describes what the network layer admits.
                  </p>
                )}
              </>
            ) : isPlaintext ? (
              <p className="text-body-sm text-text-muted" data-testid="provider-trust-none">
                This interface runs in plaintext, so it presents and verifies no certificates. Trust
                material applies only under TLS or mTLS.
              </p>
            ) : (
              <>
                {showProviderAnchor ? (
                  <>
                    <PeerTextInput
                      label="Provider CA bundle path"
                      value={draft.provider_ca_bundle_ref ?? ""}
                      mono
                      onChange={(provider_ca_bundle_ref) => patch({ provider_ca_bundle_ref })}
                      testId="provider-ca-bundle-ref"
                      placeholder="/etc/meridian/provider-certs/black-diamond-ca.pem"
                      hint="The trust anchor deciding which clients this interface will admit."
                    />
                    <ProviderCertUpload
                      onStored={(provider_ca_bundle_ref) => patch({ provider_ca_bundle_ref })}
                    />
                  </>
                ) : (
                  <p className="text-body-sm text-text-muted" data-testid="provider-trust-tls-only">
                    Under TLS, Meridian presents a server identity and verifies no client — so it
                    needs no provider CA bundle here, and every admitted connection is attributed to
                    this provider by configuration. Select mTLS to verify the client.
                  </p>
                )}
                {showMeridianIdentity && (
                  <>
                    <PeerTextInput
                      label="Meridian certificate path"
                      value={draft.meridian_cert_ref ?? ""}
                      mono
                      onChange={(meridian_cert_ref) => patch({ meridian_cert_ref })}
                      testId="provider-cert-ref"
                      placeholder="/etc/meridian/certs/provider-manager.crt"
                      // ⚠ No upload control beside this one, unlike the egress
                      // twin's. See `provider-cert-upload.tsx`: the provider-facing
                      // identity is minted at install and never uploadable
                      // (REQ-PROVIDER-035), so the BFF's route accepts CA anchors
                      // only. Stated rather than left as an unexplained asymmetry.
                      hint="A path reference only. Meridian's provider-facing identity is minted at install and is never uploaded."
                    />
                    <PeerTextInput
                      label="Meridian key path"
                      value={draft.meridian_key_ref ?? ""}
                      mono
                      onChange={(meridian_key_ref) => patch({ meridian_key_ref })}
                      testId="provider-key-ref"
                      placeholder="/etc/meridian/certs/provider-manager.key"
                      hint="A path reference only. Private keys never transit the application (ADR-015)."
                    />
                  </>
                )}
              </>
            )}
          </div>
        </PeerSection>

        <PeerSection id="provider-filter" title="Filter">
          <ProviderFilterBuilder
            root={draft.filter?.root ?? null}
            payloadDomain={payloadDomain}
            onChange={(root) => patch({ filter: toProviderFilterConfig(root) })}
          />
        </PeerSection>

        <PeerSection id="provider-state" title="State" help={PROVIDER_INTERFACE_STATE_HELP}>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <StatePill state={draft.state} />
              <PeerSuppressionNote
                resolved={resolveEffectiveState(ancestors, draft.state)}
                testId="provider-interface-detail"
              />
              {isNew ? (
                <span className="text-meta text-text-muted" data-testid="provider-state-unsaved">
                  New interfaces are created stopped. Save first, then start it deliberately.
                </span>
              ) : (
                <StateToggleButton
                  state={draft.state}
                  pending={statePending}
                  onToggle={onSetState}
                  testId="provider-interface-state-toggle"
                  label="interface"
                />
              )}
            </div>
          </div>
        </PeerSection>
      </div>

      {saveError && (
        <p
          role="alert"
          data-testid="provider-save-error"
          className="px-4 py-2 text-body-sm text-destructive"
        >
          {saveError}
        </p>
      )}

      {/* REQ-UI-161's second statement, at CONFIRMATION. Deliberately the same
          sentence as the one beside the field — one constant in the catalog, so
          the two cannot drift into describing the same wire differently — with
          the act named, because this one answers "what am I about to make true?"
          rather than "what am I choosing?". */}
      {isPlaintext && (
        <p
          role="status"
          data-testid="provider-save-plaintext-consequence"
          className="border-t border-destructive px-4 py-2 text-meta text-destructive"
        >
          Saving leaves this interface in <strong>plaintext</strong>:{" "}
          {findProviderTransportSecurity("plaintext")?.consequence}
        </p>
      )}

      <footer className="flex items-center gap-2 border-t border-border px-4 py-2">
        {!isNew &&
          (confirmDelete ? (
            <div className="flex items-center gap-2">
              <span className="text-meta text-destructive">
                Delete this interface permanently? Nothing arrives through it again.
              </span>
              <button
                type="button"
                onClick={onDelete}
                data-testid="provider-delete-confirm"
                className="rounded bg-destructive px-2 py-0.5 text-meta text-destructive-foreground hover:opacity-90"
              >
                Delete
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="rounded border border-border px-2 py-0.5 text-meta text-text-primary hover:bg-bg-elevated"
              >
                Keep
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              data-testid="provider-delete"
              className="rounded border border-destructive px-2 py-0.5 text-meta text-destructive hover:bg-destructive/10"
            >
              Delete interface
            </button>
          ))}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="provider-cancel"
            className="rounded border border-border px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={blocked || savePending}
            data-testid="provider-save"
            className="rounded bg-brand px-3 py-1 text-body-sm font-medium text-bg-base hover:bg-brand-dim disabled:opacity-50"
          >
            {savePending
              ? "Saving…"
              : isPlaintext
                ? // The button names the consequence too. An operator scanning
                  // for the primary action reads this and nothing else.
                  isNew
                  ? "Create in plaintext"
                  : "Save in plaintext"
                : isNew
                  ? "Create interface"
                  : "Save changes"}
          </button>
        </div>
      </footer>
    </form>
  );
}

/**
 * The trust-material fields implied by a ``(trust mode, wire mode)`` pair.
 *
 * One definition, applied by all three of the setters above, so a type change, a
 * trust-mode change and a wire-mode change cannot disagree about what a given
 * pair implies. Anything the pair makes inapplicable is **cleared**, not stashed:
 * a retained reference is inert configuration that reads as a control, and a
 * retained demarcation list under `mtls` trust is refused by the config model as
 * surplus — an invisible 422 waiting on the next save.
 */
function trustDependentFields(
  trustMode: TrustMode,
  wireMode: TransportSecurity,
  draft: ProviderInterfaceConfig,
): Pick<
  ProviderInterfaceConfig,
  "demarcation_ips" | "provider_ca_bundle_ref" | "meridian_cert_ref" | "meridian_key_ref"
> {
  const tunnel = trustMode === "tunnel";
  return {
    demarcation_ips: tunnel ? draft.demarcation_ips : [],
    provider_ca_bundle_ref:
      !tunnel && wireMode === "mtls" ? (draft.provider_ca_bundle_ref ?? null) : null,
    meridian_cert_ref:
      !tunnel && wireMode !== "plaintext" ? (draft.meridian_cert_ref ?? null) : null,
    meridian_key_ref: !tunnel && wireMode !== "plaintext" ? (draft.meridian_key_ref ?? null) : null,
  };
}
